/**
 * OutlookBuilderDialog — chart-side host for the outlook-driven Strategy Architect.
 * Opened when the user arms one of the 5 outlook chips and picks the level(s) on the
 * chart; it renders the (seeded, auto-running) Architect for that underlying + outlook,
 * showing ranked baskets with the full richness (provenPnl / payoff / reasoning / Greeks
 * / Fit). Picking a basket sends it to FNO → Baskets (PENDING). Draggable by its header.
 */
import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Paper, PaperProps } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import StrategyArchitect from '../options/StrategyArchitect';
import { OutlookMode } from '../../services/optionsBuilder.service';

const DraggablePaper: React.FC<PaperProps> = (props) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable nodeRef={nodeRef as React.RefObject<HTMLDivElement>} handle="#outlook-builder-handle"
      cancel={'[class*="MuiButtonBase-root"],input,[class*="MuiSelect"]'} bounds="parent">
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
};

interface Props {
  open: boolean;
  underlyingSymbol: string;
  mode: OutlookMode | null;
  level: number | null;
  level2?: number | null;
  onClose: () => void;
}

const OutlookBuilderDialog: React.FC<Props> = ({ open, underlyingSymbol, mode, level, level2, onClose }) => {
  const ready = open && !!underlyingSymbol && !!mode && level != null && level > 0;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperComponent={DraggablePaper}
      PaperProps={{ sx: { m: 1 } }}>
      <DialogTitle id="outlook-builder-handle" sx={{ cursor: 'move', display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography sx={{ fontWeight: 800 }}>Build option basket from your outlook</Typography>
        <Box sx={{ ml: 'auto' }}><IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton></Box>
      </DialogTitle>
      <DialogContent dividers>
        {ready ? (
          // `key` forces a fresh Architect (auto-build) each time the seed changes.
          <StrategyArchitect
            key={`${underlyingSymbol}-${mode}-${level}-${level2 ?? ''}`}
            compact
            seed={{ underlyingSymbol, mode: mode as OutlookMode, level: level as number, level2: level2 ?? undefined }}
            onExecuted={onClose}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Pick the level on the chart to build baskets.</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OutlookBuilderDialog;
